import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginFormData } from '../types';
import './Auth.css';

const Login: React.FC = () => {
  const { login, error, clearError, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Effacer les erreurs d'authentification lors du démontage
  useEffect(() => {
    return () => {
      clearError();
    };
  }, []);

  // Gérer les changements dans le formulaire
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Valider le champ si déjà touché
    if (touched[name]) {
      validateField(name, value);
    }
  };

  // Gérer la perte de focus
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));

    // Valider le champ
    validateField(name, value);
  };

  // Valider un champ
  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'username':
        if (!value.trim()) {
          error = 'Le nom d\'utilisateur est requis';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Le mot de passe est requis';
        }
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: error,
    }));

    return !error;
  };

  // Valider tout le formulaire
  const validateForm = () => {
    const usernameValid = validateField('username', formData.username);
    const passwordValid = validateField('password', formData.password);
    
    // Marquer tous les champs comme touchés
    setTouched({
      username: true,
      password: true,
    });

    return usernameValid && passwordValid;
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider le formulaire
    if (!validateForm()) {
      return;
    }
    
    // Soumettre le formulaire
    await login(formData);
  };

  return (
    <div className="auth-container">
      <div className="auth-card" lang="fr">
        <h1>Connexion</h1>
        
        {error && <div className="error-message" role="alert">{error}</div>}
        
        <form onSubmit={handleSubmit} className="auth-form" aria-labelledby="login-heading">
          <h2 id="login-heading" className="visually-hidden">Formulaire de connexion</h2>
          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.username && touched.username ? 'input-error' : ''}
              disabled={isLoading}
              autoComplete="username"
              aria-required="true"
              aria-invalid={!!(validationErrors.username && touched.username)}
            />
            {validationErrors.username && touched.username && (
              <div className="error-text" role="alert">{validationErrors.username}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              className={validationErrors.password && touched.password ? 'input-error' : ''}
              disabled={isLoading}
              autoComplete="current-password"
              aria-required="true"
              aria-invalid={!!(validationErrors.password && touched.password)}
            />
            {validationErrors.password && touched.password && (
              <div className="error-text" role="alert">{validationErrors.password}</div>
            )}
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
